import { Dropdown } from '@fountain/fountain-ui-components';
import {
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@material-ui/core';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import React, { VFC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import globalMessages from 'shared/global/messages';

import { messages } from './messages';
import {
  AnswerDraft,
  ratingOptions,
  ScorecardQuestion,
} from './scorecardFillForm';
import { useStyles } from './styles';
import { ValidationError } from './ValidationError';

export interface QuestionFieldProps {
  question: ScorecardQuestion;
  position: number;
  answer?: AnswerDraft;
  showError: boolean;
  onChange: (questionId: number, patch: AnswerDraft) => void;
}

// One numbered scorecard question: its input, its optional notes field, and
// its unanswered-required error.
export const QuestionField: VFC<QuestionFieldProps> = ({
  question,
  position,
  answer,
  showError,
  onChange,
}) => {
  const classes = useStyles();
  const intl = useIntl();
  const isFreeText = question.question_type === 'free_text';
  const showNotes = question.allow_notes && !isFreeText;

  const inlineControl = () => {
    switch (question.question_type) {
      case 'rating':
        return (
          <RadioGroup
            row
            aria-label={question.text}
            className={classes.ratingGroup}
            value={answer?.score?.toString() ?? ''}
            onChange={event =>
              onChange(question.id, { score: Number(event.target.value) })
            }
          >
            {ratingOptions(question).map(option => (
              <FormControlLabel
                key={option}
                className={classes.ratingOption}
                value={option.toString()}
                control={<Radio color="primary" size="small" />}
                label={option}
                labelPlacement="bottom"
              />
            ))}
          </RadioGroup>
        );
      case 'yes_no':
        return (
          <ToggleButtonGroup
            exclusive
            aria-label={question.text}
            className={classes.choiceGroup}
            value={answer?.value ?? null}
            onChange={(_event, value: string | null) =>
              onChange(question.id, { value })
            }
          >
            <ToggleButton className={classes.choiceOption} value="yes">
              <FormattedMessage {...globalMessages.yes} />
            </ToggleButton>
            <ToggleButton className={classes.choiceOption} value="no">
              <FormattedMessage {...globalMessages.no} />
            </ToggleButton>
          </ToggleButtonGroup>
        );
      default:
        return null;
    }
  };

  const blockControl = () => {
    switch (question.question_type) {
      case 'dropdown':
        return (
          <Dropdown
            aria-label={question.text}
            isClearable
            isSearchable={false}
            size="medium"
            options={question.options.map(option => ({
              value: option,
              label: option,
            }))}
            placeholder={intl.formatMessage(messages.selectAnOption)}
            value={answer?.value ?? undefined}
            onChange={(value: string | null) =>
              onChange(question.id, { value: value ?? null })
            }
          />
        );
      case 'free_text':
        return (
          <TextField
            fullWidth
            multiline
            rows={3}
            className={classes.inputControl}
            variant="outlined"
            inputProps={{ 'aria-label': question.text }}
            placeholder={question.hint_text ?? undefined}
            value={answer?.value ?? ''}
            onChange={event =>
              onChange(question.id, { value: event.target.value })
            }
          />
        );
      default:
        return null;
    }
  };

  return (
    <div
      className={classes.question}
      data-testid={`scorecard-question-${question.id}`}
    >
      <div className={classes.questionRow}>
        <div className={classes.questionHeading}>
          <Typography variant="body2" className={classes.questionTitle}>
            {`${position}. ${question.text}`}
            {question.required && (
              <span className={classes.requiredMark} aria-hidden>
                {' '}
                *
              </span>
            )}
          </Typography>
          {Boolean(question.description) && (
            <Typography variant="body2" className={classes.questionDescription}>
              {question.description}
            </Typography>
          )}
        </div>
        {inlineControl()}
      </div>
      {blockControl()}
      {showError && (
        <ValidationError>
          <FormattedMessage {...messages.questionNeedsAnswer} />
        </ValidationError>
      )}
      {showNotes && (
        <TextField
          fullWidth
          className={classes.notesInput}
          variant="outlined"
          inputProps={{
            'aria-label': intl.formatMessage(messages.enterNotes),
          }}
          placeholder={intl.formatMessage(messages.enterNotes)}
          value={answer?.note ?? ''}
          onChange={event =>
            onChange(question.id, { note: event.target.value })
          }
        />
      )}
    </div>
  );
};
